import { useState, useEffect, useMemo, useCallback } from 'react';
import { Member } from '@/types/member';
import { fetchPublicMembers, fetchTotalMembersCount, fetchMemberEnrichment, searchMembersServer } from '@/services/memberService';
import { globalCache } from '@/utils/cache';
import { useDebounce } from './useDebounce';
import { PerformanceLogger } from '@/utils/performanceLogger';
import { useOptimizedPresence } from './useOptimizedPresence';
import { UserStatus } from '@/types/presence';

const calculateProfileScore = (m: Member): number => {
  let score = 0;
  const img = m.image;
  const hasPhoto = img && !img.includes('placeholder') && !img.includes('default-avatar') && img !== '';
  score += hasPhoto ? 25 : -50;
  if (m.bio && m.bio !== '' && m.bio !== 'N/A') score += 15;
  if (m.company && m.company !== '') score += 10;
  if (m.role && m.role !== '') score += 10;
  if (m.linkedin && m.linkedin !== '') score += 5;
  if (m.website && m.website !== '') score += 5;
  if (m.phone && m.phone !== '') score += 5;
  if (m.skills && m.skills.length > 0) score += 10;
  if (m.needs && m.needs.length > 0) score += 5;
  if (m.city && m.city !== '') score += 5;
  if (m.country && m.country !== '') score += 5;
  return score;
};

export interface MemberFilters {
  searchTerm: string;
  country: string;
  sector: string;
  role: string;
  language: string;
  skill: string;
  viewMode: 'grid' | 'list' | 'map' | 'analytics';
  showFilters: boolean;
  // Extended fields to match AdvancedFilterOptions
  availability: 'all' | 'online' | 'offline' | 'busy';
  presenceStatus: UserStatus[];
  joinedDate: 'all' | 'week' | 'month' | 'year';
  activityLevel: 'all' | 'high' | 'medium' | 'low';
  compatibilityScore: number[];
  subscriptionType: 'all' | 'basic' | 'premium' | 'enterprise';
  hasPortfolio: boolean;
  hasCalendar: boolean;
  isRecommended: boolean;
  distance: number[];
  commonConnections: boolean;
}

const defaultFilters: MemberFilters = {
  searchTerm: '',
  country: '',
  sector: '',
  role: '',
  language: '',
  skill: '',
  viewMode: 'grid',
  showFilters: false,
  availability: 'all',
  presenceStatus: [],
  joinedDate: 'all',
  activityLevel: 'all',
  compatibilityScore: [0, 100],
  subscriptionType: 'all',
  hasPortfolio: false,
  hasCalendar: false,
  isRecommended: false,
  distance: [0, 1000],
  commonConnections: false,
};

const INITIAL_LOAD_SIZE = 12;
const LOAD_MORE_SIZE = 8;

// 🔥 PHASE 3: Limiter la pagination infinie
const MAX_PAGES = 5; // Maximum 5 chargements supplémentaires
const MAX_MEMBERS_LOADED = INITIAL_LOAD_SIZE + (MAX_PAGES * LOAD_MORE_SIZE); // 12 + (5 × 8) = 52 membres max

export const useMembersDirectoryOptimized = (currentUserId?: string) => {
  // Use optimized presence hook
  const { getVisiblePresences, getPresence, myPresence } = useOptimizedPresence();
  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [displayedMembers, setDisplayedMembers] = useState<Member[]>([]);
  const [filters, setFilters] = useState<MemberFilters>(defaultFilters);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalMembersCount, setTotalMembersCount] = useState({ total: 0, active: 0 });
  const [enrichedIds, setEnrichedIds] = useState<Set<string>>(new Set());
  const [isServerSearching, setIsServerSearching] = useState(false);

  // Debounce search to avoid excessive filtering
  const debouncedSearchTerm = useDebounce(filters.searchTerm, 300);

  // Cached fetch with performance logging
  const fetchMembersWithCache = useCallback(async (limit: number, offset: number) => {
    const timer = PerformanceLogger.startTimer('fetchPublicMembers');
    
    try {
      const cacheKey = `public_members_${offset}_${limit}`;
      const cached = globalCache.get<Member[]>(cacheKey);
      
      if (cached) {
        timer.end({ cached: true, memberCount: cached.length });
        return cached;
      }

      const data = await fetchPublicMembers(limit, offset);
      globalCache.set(cacheKey, data, 1800000); // 30 minutes cache
      
      timer.end({ cached: false, memberCount: data.length });
      return data;
    } catch (error) {
      timer.end({ error: true });
      throw error;
    }
  }, []);

  // Load total count once on mount (with caching)
  useEffect(() => {
    const loadTotalCount = async () => {
      try {
        const cacheKey = 'total_members_count';
        const cached = globalCache.get<{ total: number; active: number }>(cacheKey);
        
        if (cached) {
          setTotalMembersCount(cached);
          return;
        }

        const totalData = await fetchTotalMembersCount();
        globalCache.set(cacheKey, totalData, 1800000); // 30 minutes cache for total count
        setTotalMembersCount(totalData);
      } catch (err) {
        console.error('Error loading total members count:', err);
      }
    };

    loadTotalCount();
  }, []);

  // Initial load - optimized to only load what's needed
  useEffect(() => {
    const loadInitialMembers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 [useMembersDirectoryOptimized] Starting initial load...');
        
        // Load first page for pagination
        const data = await fetchMembersWithCache(INITIAL_LOAD_SIZE, 0);
        
        console.log('✅ [useMembersDirectoryOptimized] Initial members loaded:', data.length);
        
        // For grid/list views, use paginated data
        // For map/analytics, load all members lazily when switching to those views
        setAllMembers(data);
        setDisplayedMembers(data);
        setCurrentPage(0);
        setHasMore(data.length === INITIAL_LOAD_SIZE);
      } catch (err) {
        console.error('❌ [useMembersDirectoryOptimized] Error loading members:', err);
        setError('Erreur lors du chargement de l\'annuaire');
      } finally {
        setLoading(false);
      }
    };

    loadInitialMembers();
  }, [fetchMembersWithCache]);

  // Phase 3: Lazy load enrichment data for visible members
  // Create stable dependency for displayedMembers
  const displayedMembersIds = useMemo(
    () => displayedMembers.map(m => m.id).join(','),
    [displayedMembers.length]
  );

  useEffect(() => {
    const loadEnrichment = async () => {
      if (displayedMembers.length === 0) return;
      
      // Ne charger que les membres non encore enrichis
      const visibleIds = displayedMembers
        .filter(m => !enrichedIds.has(m.id))
        .map(m => m.id);
      
      if (visibleIds.length === 0) return; // Déjà tous enrichis
      
      const enrichmentData = await fetchMemberEnrichment(visibleIds);
      
      // Merge enrichment data into displayed members
      setDisplayedMembers(prev => prev.map(member => ({
        ...member,
        ...(enrichmentData[member.id] || {})
      })));
      
      // Marquer ces IDs comme enrichis
      setEnrichedIds(prev => new Set([...prev, ...visibleIds]));
    };
    
    // Delay enrichment load to prioritize initial render
    const timer = setTimeout(loadEnrichment, 500);
    return () => clearTimeout(timer);
  }, [displayedMembersIds]);

  // Reset enrichment when filters change
  useEffect(() => {
    setEnrichedIds(new Set());
  }, [debouncedSearchTerm]);

  // Enhanced load more with preloading optimization
  const loadMoreMembers = useCallback(async () => {
    // ✅ Suppression de la limite MAX_PAGES pour permettre le chargement de tous les membres
    // Les utilisateurs peuvent maintenant charger autant de membres qu'ils le souhaitent
    
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const offset = nextPage * INITIAL_LOAD_SIZE;
      
      const newMembers = await fetchMembersWithCache(LOAD_MORE_SIZE, offset);
      
      if (newMembers.length > 0) {
        setAllMembers(prev => [...prev, ...newMembers]);
        setCurrentPage(nextPage);
        setHasMore(newMembers.length === LOAD_MORE_SIZE);
        
        // Preload next page in background after 2 seconds
        setTimeout(async () => {
          if (newMembers.length === LOAD_MORE_SIZE) {
            const nextOffset = (nextPage + 1) * INITIAL_LOAD_SIZE;
            await fetchMembersWithCache(LOAD_MORE_SIZE, nextOffset);
          }
        }, 2000);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more members:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore, fetchMembersWithCache]);

  // Online members calculation (memoized) - now using real-time presence data
  const onlineMembers = useMemo(() => {
    const timer = PerformanceLogger.startTimer('calculateOnlineMembers');
    const onlineSet = new Set<string>();
    const now = new Date();
    
    // ✅ Check ALL members, not just displayed ones
    allMembers.forEach(member => {
      if (member.user_id) {
        const presence = getPresence(member.user_id);
        const timeout = presence?.activity_timeout ? new Date(presence.activity_timeout) : null;
        
        // ✅ Verify activity_timeout hasn't expired
        const isReallyActive = presence?.is_active && 
                               presence.status !== 'offline' && 
                               (!timeout || timeout > now);
        
        if (isReallyActive) {
          onlineSet.add(member.user_id);
        }
      }
    });
    
    // Include current user if online
    if (myPresence?.is_active && myPresence.status !== 'offline') {
      const myTimeout = myPresence.activity_timeout ? new Date(myPresence.activity_timeout) : null;
      if (!myTimeout || myTimeout > now) {
        onlineSet.add(myPresence.user_id);
      }
    }
    
    timer.end({ onlineCount: onlineSet.size, totalMembers: allMembers.length });
    return onlineSet;
  }, [allMembers.length, getPresence, myPresence])

  // Server-side search when debouncedSearchTerm >= 2 chars
  useEffect(() => {
    if (debouncedSearchTerm.length >= 2) {
      const doServerSearch = async () => {
        setIsServerSearching(true);
        try {
          const results = await searchMembersServer(debouncedSearchTerm, 50);
          // Apply remaining local filters on server results
          let filtered = results;
          if (filters.country) filtered = filtered.filter(m => m.country === filters.country);
          if (filters.sector) filtered = filtered.filter(m => m.industry === filters.sector || m.tags?.includes(filters.sector));
          if (filters.role) filtered = filtered.filter(m => m.role === filters.role);
          if (filters.language) filtered = filtered.filter(m => m.languages?.includes(filters.language));
          if (filters.skill) filtered = filtered.filter(m => m.skills?.includes(filters.skill));
          
          filtered.sort((a, b) => calculateProfileScore(b) - calculateProfileScore(a));
          setDisplayedMembers(filtered);
        } catch (err) {
          console.error('Server search error:', err);
        } finally {
          setIsServerSearching(false);
        }
      };
      doServerSearch();
    } else {
      // No search term: use local filtered members
      setIsServerSearching(false);
    }
  }, [debouncedSearchTerm, filters.country, filters.sector, filters.role, filters.language, filters.skill]);

  // Filtered members for non-search scenarios (local filtering)
  const filteredMembers = useMemo(() => {
    // If server search is active, don't compute local filter
    if (debouncedSearchTerm.length >= 2) return allMembers;

    const timer = PerformanceLogger.startTimer('filterMembers');
    
    try {
      let filtered = [...allMembers];

      if (filters.country) {
        filtered = filtered.filter(member => member.country === filters.country);
      }
      if (filters.sector) {
        filtered = filtered.filter(member => 
          member.industry === filters.sector ||
          (member.tags && member.tags.includes(filters.sector))
        );
      }
      if (filters.role) {
        filtered = filtered.filter(member => member.role === filters.role);
      }
      if (filters.language) {
        filtered = filtered.filter(member => 
          member.languages && member.languages.includes(filters.language)
        );
      }
      if (filters.skill) {
        filtered = filtered.filter(member => 
          member.skills && member.skills.includes(filters.skill)
        );
      }

      timer.end({ totalMembers: allMembers.length, filteredMembers: filtered.length });
      return filtered.sort((a, b) => calculateProfileScore(b) - calculateProfileScore(a));
    } catch (error) {
      timer.end({ error: true });
      return allMembers;
    }
  }, [allMembers, debouncedSearchTerm, filters]);

  // Update displayed members when local filters change (only when not server searching)
  useEffect(() => {
    if (debouncedSearchTerm.length < 2) {
      setDisplayedMembers(filteredMembers);
    }
  }, [filteredMembers, debouncedSearchTerm]);

  // Filter values for dropdowns (memoized)
  const filterValues = useMemo(() => {
    const countries = [...new Set(allMembers.map(member => member.country))].filter(Boolean).sort();
    const industryValues = allMembers.map(member => member.industry).filter(Boolean);
    const tagValues = allMembers.flatMap(member => member.tags || []);
    const predefinedSectors = ['Communication', 'Relations Publiques'];
    const sectors = [...new Set([...industryValues, ...tagValues, ...predefinedSectors])].sort();
    const roles = [...new Set(allMembers.map(member => member.role))].filter(Boolean).sort();
    const languages = [...new Set(allMembers.flatMap(member => member.languages || []))].sort();
    const skills = [...new Set(allMembers.flatMap(member => member.skills || []))].sort();

    return { countries, sectors, roles, languages, skills };
  }, [allMembers]);

  // Stats calculation (memoized) - Use real totals from database
  const memberStats = useMemo(() => {
    return {
      total: totalMembersCount.total,
      active: totalMembersCount.active,
      online: onlineMembers.size,
      newThisMonth: Math.floor(totalMembersCount.total * 0.08) // Mock data based on real total
    };
  }, [totalMembersCount, onlineMembers.size]);

  // Load all members when switching to map/analytics views
  useEffect(() => {
    const loadAllMembersForViews = async () => {
      if ((filters.viewMode === 'map' || filters.viewMode === 'analytics') && allMembers.length < 100) {
        try {
          const allData = await fetchMembersWithCache(1000, 0);
          setAllMembers(allData);
        } catch (err) {
          console.error('Error loading all members for map/analytics:', err);
        }
      }
    };

    loadAllMembersForViews();
  }, [filters.viewMode, fetchMembersWithCache, allMembers.length]);

  // Actions
  const updateFilters = useCallback((newFilters: Partial<MemberFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const setViewMode = useCallback((mode: 'grid' | 'list' | 'map' | 'analytics') => {
    setFilters(prev => ({ ...prev, viewMode: mode }));
  }, []);

  const toggleFilters = useCallback(() => {
    setFilters(prev => ({ ...prev, showFilters: !prev.showFilters }));
  }, []);

  const updateSearchTerm = useCallback((searchTerm: string) => {
    updateFilters({ searchTerm });
  }, [updateFilters]);

  return {
    // Data
    members: displayedMembers,
    allMembers,
    loading,
    loadingMore,
    error,
    hasMore,
    
    // Filters
    filters,
    filterValues,
    
    // Stats
    memberStats,
    onlineMembers,
    
    // Actions
    updateFilters,
    resetFilters,
    setViewMode,
    toggleFilters,
    updateSearchTerm,
    loadMoreMembers,
  };
};
